// src/services/api.js
import axios from 'axios';

const api = axios.create({
    // Por defecto, ruta relativa: en desarrollo la sirve el proxy de React
    // ("proxy" en package.json) y en produccion el nginx que publica la web.
    baseURL: process.env.REACT_APP_API_URL || '/api',
    withCredentials: true, // 🚀 VITAL: Envío automático de cookies seguras (accessToken/refreshToken)
    // 🛡️ Cabecera anti-CSRF: el backend exige esta cabecera en POST/PUT/PATCH/DELETE.
    // Un formulario cross-site no puede fijarla sin preflight CORS (que rechazamos).
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error);
        else prom.resolve(token);
    });
    failedQueue = [];
};

const formatError = (error) => {
    const backendError = error.response?.data;
    let mensajeError = 'Error de conexión con el servidor clínico';

    if (backendError) {
        // Algunas respuestas (proxies, limitadores antiguos) llegan como texto plano
        if (typeof backendError === 'string' && backendError.trim() && !backendError.trim().startsWith('<')) {
            mensajeError = backendError.trim();
        } else if (typeof backendError.message === 'string') {
            mensajeError = backendError.message;
        } else if (typeof backendError.error === 'string') {
            mensajeError = backendError.error;
        } else if (Array.isArray(backendError.errors)) {
            mensajeError = backendError.errors
                .map(e => `${e.campo || (e.path && e.path.join('.')) || 'Campo'}: ${e.mensaje || e.message}`)
                .join(' | ');
        }
    }
    const resultado = new Error(mensajeError);
    if (backendError && typeof backendError.codigo === 'string') resultado.codigo = backendError.codigo;
    return resultado;
};

// =================================================================
// 🛡️ INTERCEPTOR DE RESPUESTAS ADAPTADO AL NUEVO BACKEND DTO
// =================================================================
api.interceptors.response.use(
    (response) => {
        const payload = response.data;

        // 🟢 Caso A: Flujos de Autenticación Crítica (login / me / 2FA)
        if (payload && (payload.user || payload.requires2FA)) {
            return payload;
        }

        // 🟢 Caso B: Respuestas en formato Array puro del backend
        if (Array.isArray(payload)) {
            return payload;
        }

        // 🟢 Caso C: NUEVA PAGINACIÓN UNIFICADA ({ data, total, page, totalPages })
        // Extraemos la colección 'data' pero inyectamos los metadatos de control contable y de triaje
        if (payload && payload.data && Array.isArray(payload.data)) {
            const listaLimpia = payload.data;

            // Inyectamos como propiedad no-enumerable para que no interfiera con iteradores o .map() de React
            Object.defineProperty(listaLimpia, 'pagination', {
                value: {
                    totalDocs: payload.total || payload.totalDocs || 0,
                    limit: payload.limit || listaLimpia.length,
                    page: payload.page || 1,
                    totalPages: payload.totalPages || 1
                },
                enumerable: false,
                writable: true
            });
            return listaLimpia;
        }

        return payload;
    },
    async (error) => {
        const originalRequest = error.config;

        // 🛡️ FIX B4: en cancelaciones o errores de red exóticos, error.config puede
        // venir undefined → leer originalRequest.url lanzaría un TypeError DENTRO del
        // propio interceptor, enmascarando el error real. Guarda defensiva primero.
        if (!originalRequest || !originalRequest.url) {
            return Promise.reject(formatError(error));
        }

        if (originalRequest.url.includes('/auth/login')) {
            return Promise.reject(formatError(error));
        }

        // 🔄 MUTEX LOCK DE REFRESH TOKEN INTELIGENTE
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh')) {

            if (isRefreshing) {
                return new Promise(function(resolve, reject) {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return api(originalRequest);
                }).catch(err => {
                    return Promise.reject(err);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Sincronización limpia con cookies en ráfaga concurrente
                await axios.post(`${api.defaults.baseURL}/auth/refresh`, {}, { withCredentials: true });

                isRefreshing = false;
                processQueue(null, true);

                return api(originalRequest);

            } catch (refreshError) {
                isRefreshing = false;
                const sessionError = new Error('Tu sesión ha caducado por inactividad o fin de turno administrativo.');
                processQueue(sessionError, null);

                // 🎯 COMUNICACIÓN ARQUITECTÓNICA: Avisamos al AuthContext para una purga controlada en memoria
                window.dispatchEvent(new Event('auth_session_expired'));

                return Promise.reject(sessionError);
            }
        }

        return Promise.reject(formatError(error));
    }
);

export default api;